import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { SaasTenantPicker } from './SaasTenantPicker.js';

interface FeatureRow {
  readonly featureId: string;
  readonly saasTenantId: string;
  readonly featureKey: string;
  readonly featureName: string | null;
  readonly featureCategory: string;
  readonly status: string;
}

const CATEGORIES = ['MODULE', 'API', 'MOBILE', 'PORTAL', 'AI', 'ANALYTICS', 'INTEGRATION', 'EXPERIMENTAL'];
const STATUSES = ['ENABLED', 'DISABLED'];
const PAGE = 25;

/**
 * M43 §27 Feature Management Dashboard (§11 Feature Management Framework).
 * Operators enable / disable per-tenant feature entitlements. Viewing needs
 * FEATURE_VIEW; enable/disable need FEATURE_MANAGE.
 */
export function FeatureManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<FeatureRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [saasTenantId, setSaasTenantId] = useState('');
  const [featureKey, setFeatureKey] = useState('');
  const [featureCategory, setFeatureCategory] = useState('MODULE');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('featureCategory', categoryFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: FeatureRow[]; total: number }>(`/v1/saas/features?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, categoryFilter, page, reload]);

  const upsert = async (enabled: boolean, e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!saasTenantId.trim()) { setActionError('A tenant id is required.'); return; }
    if (!featureKey.trim()) { setActionError('A feature key is required.'); return; }
    try {
      await apiRequest('/v1/saas/features', {
        method: 'POST',
        body: { saasTenantId: saasTenantId.trim(), featureKey: featureKey.trim(), featureCategory, enabled },
        token,
      });
      setNotice(enabled ? 'Feature enabled.' : 'Feature disabled.');
      setFeatureKey('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to manage features (FEATURE_MANAGE required).');
      else setActionError((err as Error).message);
    }
  };

  const toggle = async (row: FeatureRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    const next = row.status !== 'ENABLED';
    try {
      await apiRequest('/v1/saas/features', {
        method: 'POST',
        body: { saasTenantId: row.saasTenantId, featureKey: row.featureKey, featureCategory: row.featureCategory, enabled: next },
        token,
      });
      setNotice(next ? 'Feature enabled.' : 'Feature disabled.');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to manage features (FEATURE_MANAGE required).');
      else setActionError((err as Error).message);
    }
  };

  return (
    <main aria-label="feature-management-dashboard">
      <h1>Feature Management Dashboard</h1>

      <section aria-label="feature-upsert">
        <h2>Set Entitlement</h2>
        <form aria-label="feature-form" onSubmit={(e) => void upsert(true, e)}>
          <SaasTenantPicker label="Tenant" value={saasTenantId} onPick={setSaasTenantId} />
          <label>Feature key<input aria-label="field-feature-key" value={featureKey} onChange={(e) => setFeatureKey(e.target.value)} /></label>
          <label>Category
            <select aria-label="field-category" value={featureCategory} onChange={(e) => setFeatureCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-enable">Enable</button>
          <button type="button" aria-label="submit-disable" onClick={() => void upsert(false)}>Disable</button>
        </form>
        {actionError && <p role="alert" aria-label="feature-action-error">{actionError}</p>}
        {notice && <p aria-label="feature-notice">{notice}</p>}
      </section>

      <section aria-label="feature-filters">
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Category
          <select aria-label="filter-category" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="feature-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="feature-total">{total} feature(s).</p>
          <table aria-label="feature-table">
            <thead><tr><th scope="col">Key</th><th scope="col">Category</th><th scope="col">Status</th><th scope="col">Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4}>No features.</td></tr>
              ) : rows.map((f) => (
                <tr key={f.featureId} data-feature={f.featureId}>
                  <td aria-label={`feature-key-${f.featureId}`}>{f.featureKey}</td>
                  <td>{f.featureCategory}</td>
                  <td aria-label={`feature-status-${f.featureId}`}>{f.status}</td>
                  <td><button type="button" aria-label={`toggle-${f.featureId}`} onClick={() => void toggle(f)}>{f.status === 'ENABLED' ? 'Disable' : 'Enable'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="feature-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
